import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
});
// Auth rides on the httpOnly session cookie (same-origin) — no client-readable
// token storage, which keeps credentials out of XSS reach.

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

/** Resolve image URLs: if it's a backend-hosted /api/... path, prefix with the backend URL. */
export function resolveImg(url) {
  if (!url) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
}

/** Like resolveImg but appends `?w=<size>` so the backend serves a resized
 *  WebP. Use for thumbnails / cards / category tiles. Falls through cleanly
 *  for non-/api/media URLs (returns the original). */
export function resolveImgSized(url, width = 800) {
  if (!url) return url;
  if (!url.includes("/api/media/")) return resolveImg(url);
  const sep = url.includes("?") ? "&" : "?";
  return resolveImg(`${url}${sep}w=${width}`);
}

/** Trigger a browser file download for an authenticated API path.
 *  Plain <a href> can't send bearer tokens, so we fetch as a Blob, derive
 *  the filename from Content-Disposition (or the caller's fallback), and
 *  programmatically click an <a> with object-URL href.
 *  Returns a promise that resolves once the download starts. Throws if
 *  the request fails so callers can show a toast. */
export async function downloadAuthFile(apiPath, fallbackName = "download") {
  const { data, headers } = await api.get(apiPath, { responseType: "blob" });
  // Filename from header (RFC 5987 / 2616 forms) or fallback
  let filename = fallbackName;
  const cd = headers["content-disposition"] || headers["Content-Disposition"];
  if (cd) {
    const m =
      cd.match(/filename\*=UTF-8''([^;]+)/i) ||
      cd.match(/filename="?([^";]+)"?/i);
    if (m) filename = decodeURIComponent(m[1]);
  }
  const blob = new Blob([data], { type: data.type || "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 1000);
}
